

/**
 * テスト用にプライベート関数をエクスポート
 */
export const __test__ = {
    findLocalMax,
    findLocalMin,
    dropNearTargets,
    dropContinuallyTargets,
    dropHighValueInContinuallyTargets,
    addNextTargets,
    addPreviousTargets,
    returnIndexes,
    addTargetsBetweenLongDistance,
    dropLastPartForOneDay,
    returnRankOfPoolUrine,
    returnRankOfUrination,
    dropDuplicatedUrination,
    dropNullDataTargets,
    dropLowRankPoolUrine,
    dropLowRankUrination
};

/**
 * 排尿イベントを検出し、尿が溜まっている傾向と排尿傾向のフラグを返します
 *
 * @param percents - 閾値のパーセント値の配列
 * @param means - 尿量の平均値の配列
 * @param successUrinations - 成功した排尿回数の配列
 * @param dataCounts - 平均データのカウント配列
 * @param concatData - trueの場合、プール尿と排尿の間の重複した排尿をドロップします
 * @returns 尿が溜まっている傾向(poolUrine)と排尿傾向(urination)のフラグを含むオブジェクト
 */
export default function detectUrinationEvents(
    percents: number[],
    means: number[],
    successUrinations: number[],
    dataCounts: number[],
    concatData: boolean = true
): Record<string, boolean[]> {
    const [, poolUrine] = returnPoolUrine(percents, means, successUrinations);
    const [, urination] = returnUrination(means, percents, dataCounts);

    dropLowRankPoolUrine(poolUrine, percents, means, dataCounts);
    dropLowRankUrination(urination, percents, means, dataCounts);

    if (concatData) {
        dropDuplicatedUrination(poolUrine, urination);
        moveLongDistanceUrination(poolUrine, urination, percents, means);
        dropConflictedUrination(poolUrine, urination);
    }

    return {
        "poolUrine": poolUrine,
        "urination": urination
    };
}

/**
 * 排尿イベントのデータを処理し、差分と条件配列を返します
 * @param means - 平均値の配列
 * @param percents - パーセント値の配列
 * @param dataCounts - データ数の配列
 * @returns [差分値の配列, 条件の真偽値配列]
 */
function returnUrination(means: number[], percents: number[], dataCounts: number[]): [number[], boolean[]] {
    // 差分の計算（最初に0.0を追加し、その後各要素の差分を計算）
    const diffs: number[] = [0.0];
    for (let i = 1; i < percents.length; i++) {
        diffs.push(percents[i] - percents[i - 1]);
    }

    // 条件配列の作成（差分が負の値かどうか）
    const targets: boolean[] = diffs.map((diff) => diff < 0);

    // 各種処理の適用
    findLocalMin(targets, percents, diffs);
    dropNearTargets(targets, means, percents, "max", 3);
    dropHighValueInContinuallyTargets(targets, means);
    dropNearTargets(targets, means, percents, "max", 3);
    dropNullDataTargets(targets, dataCounts);

    return [diffs, targets];
}

/**
 * プール内の尿データを処理し、差分と条件配列を返します
 * @param percents - パーセント値の配列
 * @param means - 平均値の配列
 * @param successUrinations - 成功した排尿イベントの配列
 * @returns [差分値の配列, 条件の真偽値配列]
 */
function returnPoolUrine(
    percents: number[],
    means: number[],
    successUrinations: number[]
): [number[], boolean[]] {
    // 差分の計算（最初の要素を追加し、その後各要素の差分を計算）
    const diffs: number[] = [percents[0]];
    for (let i = 1; i < percents.length; i++) {
        diffs.push(percents[i] - percents[i - 1]);
    }

    // 条件配列の作成（差分が正の値かどうか）
    const targets: boolean[] = diffs.map((diff) => diff > 0);

    // 各種処理の適用
    findLocalMax(targets, percents, diffs);
    dropNearTargets(targets, percents, means, "min", 4, successUrinations);
    dropContinuallyTargets(targets, means);
    addNextTargets(targets, percents, means);
    addPreviousTargets(targets, means, diffs);
    addTargetsBetweenLongDistance(targets, [means, percents]);
    dropNearTargets(targets, percents, means, "min", 2);
    dropLastPartForOneDay(targets);

    return [diffs, targets];
}


/**
 * ローカル最大値を検索し、条件配列を更新します
 * @param targets - 条件の真偽値配列（更新対象）
 * @param percents - パーセント値の配列
 * @param diffs - 差分値の配列
 */
function findLocalMax(targets: boolean[], percents: number[], diffs: number[]): void {
    // 負の差分を持つインデックスとdiffsの長さを含む配列を作成
    const minusDiffIndexes: number[] = diffs
        .map((d, i) => ({ diff: d, index: i }))
        .filter((item) => item.diff < -1)
        .map((item) => item.index);
    minusDiffIndexes.push(diffs.length);

    // 各ターゲットインデックスを処理
    for (let i = 0; i < targets.length; i++) {
        if (targets[i]) {
            // 最大のマイナス差分インデックスがiより大きい場合
            if (Math.max(...minusDiffIndexes) > i) {
                // iより大きいインデックスの中で最小のものを見つける
                const largerIndexes = minusDiffIndexes.filter((idx) => idx > i);
                const nearestMinusIndex = Math.min(...largerIndexes);

                // 範囲内のパーセント値を取得
                const percentsInRange = percents.slice(i, nearestMinusIndex);

                if (percentsInRange.length > 1) {
                    const maxInRange = Math.max(...percentsInRange);

                    // 範囲内の最大値が現在位置より大きい場合
                    if (maxInRange > percents[i]) {
                        targets[i] = false;
                    }
                    // 範囲内のすべての値が最大値と等しい場合
                    else if (percentsInRange.every((p) => p === maxInRange)) {
                        for (let j = i + 1; j < nearestMinusIndex; j++) {
                            targets[j] = true;
                        }
                    }
                }
            }
        }
    }
}

/**
 * 近接するターゲット間で特定の条件に基づいて一方を無効化します
 * @param targets - 更新される真偽値配列
 * @param firstValues - 比較に使用する第一の値の配列
 * @param secondValues - 比較に使用する第二の値の配列
 * @param droppedStatic - ドロップするターゲットを決定する方法 ("min" または "max")
 * @param rangeUpperLimit - 近接と見なす最大距離
 * @param additionalTargets - オプションの追加ターゲット配列
 */
function dropNearTargets(
    targets: boolean[],
    firstValues: number[],
    secondValues: number[],
    droppedStatic: string,
    rangeUpperLimit: number,
    additionalTargets?: number[]
): void {
    // droppedStaticに基づいて使用する関数を選択
    const dropStatic: Record<string, (arr: number[]) => number> = {
        "min": (arr: number[]) => {
            const minValue = Math.min(...arr);
            return arr.indexOf(minValue);
        },
        "max": (arr: number[]) => {
            const maxValue = Math.max(...arr);
            return arr.indexOf(maxValue);
        }
    };

    // 最後に処理したインデックスを追跡するための変数
    let latest: number | null = null;

    // すべてのターゲット位置を順に処理
    for (let i = 0; i < targets.length; i++) {
        if (targets[i]) {
            if (latest === null) {
                latest = i;
            } else if (1 < i - latest && i - latest <= rangeUpperLimit) {
                // 近接しているターゲットの処理
                let droppedIndex: number;

                if (firstValues[i] === firstValues[latest]) {
                    // 第一の値が同じ場合
                    if (additionalTargets === undefined || secondValues[i] !== secondValues[latest]) {
                        // 追加ターゲットがない、または第二の値が異なる場合
                        const droppedTarget = [secondValues[i], secondValues[latest]];
                        const dropIndex = dropStatic[droppedStatic](droppedTarget);
                        droppedIndex = [i, latest][dropIndex];
                    } else {
                        // 追加ターゲットがあり、第二の値が同じ場合
                        droppedIndex = additionalTargets[i] < additionalTargets[latest] ? i : latest;
                    }
                } else {
                    // 第一の値が異なる場合
                    const droppedTarget = [firstValues[i], firstValues[latest]];
                    const dropIndex = dropStatic[droppedStatic](droppedTarget);
                    droppedIndex = [i, latest][dropIndex];
                }

                // 選択されたターゲットを無効化
                targets[droppedIndex] = false;
            } else {
                // 近接していない場合、最新の処理済みインデックスを更新
                latest = i;
            }
        }
    }
}

/**
 * 連続するターゲットを処理し、条件に基づいて一部を無効化します
 * @param targets - 更新される真偽値配列
 * @param means - 比較に使用する値の配列
 * @param findType - 使用する関数のタイプ ("max" または "min")
 */
function dropContinuallyTargets(
    targets: boolean[],
    means: number[],
    findType: string = "max"
): void {
    // 指定されたfindTypeに基づいて使用する関数を選択
    const localFunc: Record<string, (arr: number[]) => number> = {
        "max": (arr: number[]) => Math.max(...arr),
        "min": (arr: number[]) => Math.min(...arr)
    };

    let continue_ = 0;

    // タイプガードを行い、undefinedでないことを確認
    const selectedFunc = localFunc[findType];
    if (!selectedFunc) {
        throw new Error(`Invalid findType: ${findType}`);
    }

    // targetsの各要素を処理（最後にfalseを追加）
    [...targets, false].forEach((target, i) => {
        if (target) {
            // trueの場合、連続カウントを増加
            continue_ += 1;
        } else {
            // falseの場合で、3つ以上連続していた場合の処理
            if (continue_ >= 3) {
                let findLocalValue = false;
                let breakLoop = false;

                // 連続したtrueの範囲を処理
                for (let j = i - continue_; j < i && !breakLoop; j++) {
                    // 範囲内のローカル最大/最小値を見つける
                    const rangeValues = means.slice(i - continue_, i);
                    const localValue = selectedFunc(rangeValues);

                    if (means[j] === localValue) {
                        findLocalValue = true;
                    } else if (findLocalValue) {
                        // ローカル値を見つけた後の要素を無効化
                        for (let k = j; k < i; k++) {
                            targets[k] = false;
                        }
                        breakLoop = true; // ループを中断
                    } else {
                        // ローカル値が見つかる前の要素を無効化
                        targets[j] = false;
                    }
                }
            }
            // 連続カウントをリセット
            continue_ = 0;
        }
    });
}

/**
 * 条件に基づいて次のインデックスのターゲットを有効化します
 * @param targets - 更新される真偽値配列
 * @param percents - パーセント値の配列
 * @param means - 平均値の配列
 */
function addNextTargets(
    targets: boolean[],
    percents: number[],
    means: number[]
): void {
    const isNextTargetEligible = (index: number) => {
        if (index < targets.length - 1) {
            return means[index] < means[index + 1] && percents[index] - percents[index + 1] < 20;
        }
        return false;
    }
    targets.slice(0, -1).forEach((targetFlag, i) => {
        if (targetFlag && isNextTargetEligible(i)) {
            targets[i + 1] = true;
        }
    });
}

/**
 * 条件に基づいて前のインデックスのターゲットを有効化します
 * @param targets - 更新される真偽値配列
 * @param means - 平均値の配列
 * @param diffs - 差分値の配列
 */
function addPreviousTargets(
    targets: boolean[],
    means: number[],
    diffs: number[]
): void {
// 最初のインデックスを除く全てのインデックスを処理
    for (let i = 1; i < targets.length; i++) {
        if (targets[i]) {
            // 現在の平均値と前の平均値の差が1未満、かつ前の差分が30より大きい場合
            if (means[i] - means[i - 1] < 1 && diffs[i - 1] > 30) {
                targets[i - 1] = true;
            }
        }
    }
}

/**
 * 真の値でその次が偽の値になっているインデックスを返します
 * @param targets - 入力の真偽値配列
 * @returns 条件を満たすインデックスを示す真偽値配列
 */
function returnIndexes(targets: boolean[]): boolean[] {
    // 配列をシフトして、最後に偽の値を追加
    const shiftedTargets = [...targets.slice(1), false];

    // 元の配列とシフトした配列をペアにして、条件を適用
    return targets.map((current, index) => {
        const next = shiftedTargets[index];
        return current && !next;
    });
}

/**
 * 長距離間にあるターゲット間に新しいターゲットを追加します
 * @param targets - 更新される真偽値配列
 * @param keyValues - キー値の二次元配列
 * @param distanceLowerLimit - ターゲット間の最小距離閾値（デフォルト: 12）
 * @param marginRange - 余白範囲（デフォルト: 4）
 */
function addTargetsBetweenLongDistance(
    targets: boolean[],
    keyValues: number[][],
    distanceLowerLimit: number = 12,
    marginRange: number = 4
): void {
    // trueとなっているインデックスを抽出
    const targetIndexes: number[] = [];
    targets.forEach((t, i) => {
        if (t) {
            targetIndexes.push(i);
        }
    });

    // 隣接するターゲットインデックスのペアごとに処理
    for (let i = 0; i < targetIndexes.length - 1; i++) {
        const start = targetIndexes[i];
        const end = targetIndexes[i + 1];

        // 距離が閾値を超えているか確認
        if (end - start > distanceLowerLimit) {
            // スライスした範囲のデータを取得
            const slicedValues = keyValues.map((row) =>
                row.slice(start + marginRange + 1, end - marginRange));

            // 転置して行単位ではなく列単位のデータにする
            const transposed: number[][] = [];
            for (let col = 0; col < slicedValues[0].length; col++) {
                const columnValues: number[] = [];
                for (let row = 0; row < slicedValues.length; row++) {
                    columnValues.push(slicedValues[row][col]);
                }
                transposed.push(columnValues);
            }

            // 値でソートしてインデックスを取得
            const sortedIndices = transposed
                .map((row, idx) => ({ values: row, index: idx }))
                .sort((a, b) => {
                    // Scalaコードの(row(1), row.head)に相当する比較
                    if (a.values[1] !== b.values[1]) {
                        return a.values[1] - b.values[1];
                    }
                    return a.values[0] - b.values[0];
                })
                .map((item) => item.index);

            // 最後のインデックスを取得（ソート後の最大値に相当）
            const lastIndex = sortedIndices[sortedIndices.length - 1];
            const localTargetIdx = start + marginRange + 1 + lastIndex;

            // 条件を満たせばターゲットを追加
            if (keyValues[keyValues.length - 1][localTargetIdx] > 0) {
                targets[localTargetIdx] = true;
            }
        }
    }
}

/**
 * 1日分のデータについて、末尾の連続するtrueの値をfalseに変更します
 * @param targets - 更新される真偽値配列（長さが48で先頭と末尾がtrueの場合のみ処理）
 */
function dropLastPartForOneDay(targets: boolean[]): void {
    // 長さが48で最初と最後の要素がtrueの場合のみ処理
    if (targets.length === 48 && targets[targets.length - 1] && targets[0]) {
        // 末尾から逆順に処理
        for (let i = targets.length - 1; i >= 0; i--) {
            if (targets[i]) {
                targets[i] = false;
            } else {
                // falseを見つけたらループを終了
                break;
            }
        }
    }
}

function dropLowRankPoolUrine(
    targets: boolean[],
    percents: number[],
    means: number[],
    dataCounts: number[]
): void {
    const [poolUrineRanks, _unusedPoolUrineIndexes] = returnRankOfPoolUrine(targets, percents, means, dataCounts);

    dropLowRankFlags(targets, poolUrineRanks)
}

/**
 * プールの尿のランクを返す関数
 * @param targets - 真偽値の配列
 * @param percents - パーセント値の配列
 * @param means - 平均値の配列
 * @param dataCounts - データ数の配列
 * @returns [ランクの配列, インデックスの配列]
 */
function returnRankOfPoolUrine(
    targets: boolean[],
    percents: number[],
    means: number[],
    dataCounts: number[]
): [number[], boolean[]] {
    const indexes = returnIndexes(targets);
    const rankOfIdx = searchRankOfIdx(
        [dataCounts as any[], means as any[], percents as any[]],
        indexes
    );
    return [convertIdxToRank(rankOfIdx), indexes];
}

/**
 * 特徴リストとターゲットに基づいてランクのインデックスを検索
 * @param featuresList - 特徴の二次元配列
 * @param targets - 真偽値の配列
 * @returns インデックスの配列
 */
function searchRankOfIdx(
    featuresList: any[][],
    targets: boolean[]
): number[] {
    // カスタム比較関数
    const compare = (x: any, y: any): number => {
        if (typeof x === 'number' && typeof y === 'number') {
            return x - y;
        }
        return String(x).localeCompare(String(y));
    };

    // 特徴配列を転置
    const features = featuresList[0].map((_, colIndex) =>
        featuresList.map((row) => row[colIndex]));

    // 変更インデックスを収集
    const changeIndices = [0].concat(
        targets.slice(1).reduce((acc: number[], curr, i) => {
            if (curr !== targets[i]) {
                acc.push(i + 1);
            }
            return acc;
        }, [])
    );

    // 真偽値を収集
    const bools = changeIndices.map((i) => targets[i]);

    // ターゲットの最大値を収集
    const maxsOfTargets = changeIndices
        .filter((_, i) => bools[i])
        .map((changeIndex) => features[changeIndex]);

    // ソートしてインデックスを返す
    return maxsOfTargets
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
            // 第3要素、第2要素、第1要素の順で比較
            const comp2 = compare(a.row[2], b.row[2]);
            if (comp2 !== 0) return comp2;
            const comp1 = compare(a.row[1], b.row[1]);
            if (comp1 !== 0) return comp1;
            return compare(a.row[0], b.row[0]);
        })
        .map((item) => item.index);
}

/**
 * インデックスをランクに変換
 * @param idx - インデックスの配列
 * @returns ランクの配列
 */
function convertIdxToRank(idx: number[]): number[] {
    const ranks = new Array(idx.length).fill(0);
    idx.forEach((rank, i) => {
        ranks[rank] = i;
    });
    const maxRank = Math.max(...ranks);
    return ranks.map((r) => maxRank - r + 1);
}

/**
 * ローカル最小値を検索し、条件配列を更新します
 * @param targets - 条件の真偽値配列（更新対象）
 * @param percents - パーセント値の配列
 * @param diffs - 差分値の配列
 */
function findLocalMin(targets: boolean[], percents: number[], diffs: number[]): void {
    // 正の差分を持つインデックスとdiffsの長さを含む配列を作成
    const plusDiffIndexes: number[] = diffs
        .map((d, i) => ({ diff: d, index: i }))
        .filter((item) => item.diff > 0)
        .map((item) => item.index);
    plusDiffIndexes.push(diffs.length);

    // 各ターゲットインデックスを処理
    for (let i = 0; i < targets.length; i++) {
        if (targets[i]) {
            // 最大の正の差分インデックスがiより大きい場合
            if (Math.max(...plusDiffIndexes) > i) {
                // iより大きいインデックスの中で最小のものを見つける
                const largerIndexes = plusDiffIndexes.filter((idx) => idx > i);
                const nearestPlusIndex = Math.min(...largerIndexes);

                // 範囲内のパーセント値を取得
                const percentsInRange = percents.slice(i, nearestPlusIndex);

                if (percentsInRange.length > 1) {
                    const minInRange = Math.min(...percentsInRange);

                    // 範囲内の最小値が現在位置より小さい場合
                    if (minInRange < percents[i]) {
                        targets[i] = false;
                    }
                    // 範囲内のすべての値が最小値と等しい場合
                    else if (percentsInRange.every((p) => p === minInRange)) {
                        for (let j = i + 1; j < nearestPlusIndex; j++) {
                            targets[j] = true;
                        }
                    }
                }
            }
        }
    }
}

/**
 * 連続するターゲットの中で高い値を持つものを無効化し、適切な値を保持します
 * @param targets - 更新される真偽値配列
 * @param means - 平均値の配列
 */
function dropHighValueInContinuallyTargets(targets: boolean[], means: number[]): void {
    let continue_ = 0;

    // targetsに false を追加した配列を作成してループ処理
    [...targets, false].forEach((target, i) => {
        if (target) {
            continue_ += 1;
        } else if (continue_ >= 2) {
            // スライスの範囲を取得し、差分を計算
            const slice = means.slice(i - continue_, i);
            const localDiffs = [0.0, ...slice.slice(1).map((v, j) => v - slice[j])];

            // 最初の正の値を持つインデックスを探す
            let localMinIdx = localDiffs.findIndex((d) => d > 0);
            if (localMinIdx === -1) localMinIdx = localDiffs.length;
            localMinIdx += i - continue_ - 1;

            // 同じ値が続く場合のインデックス調整
            for (let j = localMinIdx - 1; j >= i - continue_; j--) {
                if (means[j] === means[localMinIdx]) {
                    localMinIdx--;
                } else {
                    break;
                }
            }

            // ターゲットの更新
            for (let j = i - continue_; j < i; j++) {
                targets[j] = false;
            }
            targets[localMinIdx] = true;
            continue_ = 0;
        }
    });
}

function dropNullDataTargets(targets: boolean[], dataCounts: number[]): void {

    for (let i = dataCounts.length - 1; i >= 0; i--) {
        if (dataCounts[i] === 0 && targets[i]) {
            targets[i] = false;
        }
        if (dataCounts[i] > 0) {
            break;
        }
    }

}

function dropLowRankUrination(
    targets: boolean[],
    percents: number[],
    means: number[],
    dataCounts: number[]
): void {
    const [urinationRanks, _unusedUrinationIndexes] = returnRankOfUrination(targets, percents, means, dataCounts);

    dropLowRankFlags(targets, urinationRanks)
}

function returnRankOfUrination(targets: boolean[], percents: number[], means: number[], dataCounts: number[]): [number[], boolean[]] {
    const indexes = returnIndexes(targets);

    const rankOfIdx = searchRankOfIdx(
        [
            dataCounts,
            means.map((m) => 10 - m),
            percents.map((p) => 100 - p)
        ],
        indexes
    );

    return [convertIdxToRank(rankOfIdx), indexes];
}

/**
 * 重複する排尿イベントを削除します
 * @param poolUrine - プール内の尿検出の真偽値配列
 * @param urination - 排尿イベントの真偽値配列（更新対象）
 */
function dropDuplicatedUrination(
    poolUrine: boolean[],
    urination: boolean[],
): void {
    // 尿検出インデックスを取得し、配列の長さを追加
    const urineIndexes = returnIndexes(poolUrine)
        .map((isTrue, i) => ({ isTrue, i }))
        .filter((item) => item.isTrue)
        .map((item) => item.i);
    urineIndexes.push(poolUrine.length);


    // 最初の尿検出インデックス前の排尿イベントをすべて無効化
    for (let i = 0; i < urineIndexes[0]; i++) {
        urination[i] = false;
    }

    // 隣接する尿検出インデックス間で処理
    for (let i = 0; i < urineIndexes.length - 1; i++) {
        const start = urineIndexes[i];
        const end = urineIndexes[i + 1];

        // 範囲内の排尿イベント数をカウント
        const urinationCount = urination
            .slice(start, end)
            .filter(Boolean)
            .length;


        // 排尿イベントが2つ以上ある場合
        if (urinationCount >= 2) {
            let dropFlag = false;

            // 範囲内の各排尿イベントを処理
            for (let j = start; j < end; j++) {
                if (urination[j]) {
                    if (!dropFlag) {
                        // 最初の排尿イベントはフラグをオンにするだけ
                        dropFlag = true;
                    } else {
                        // 2つ目以降の排尿イベントは無効化
                        urination[j] = false;
                    }
                }
            }
        }
    }
}

/**
 * poolUrineと離れているurinationを移動
 * @param poolUrine - プール内の尿検出の真偽値配列
 * @param urination - 排尿イベントの真偽値配列（更新対象）
 * @param percents - 閾値のパーセント値の配列
 * @param means - 尿量の平均値の配列
 */
function moveLongDistanceUrination(
    poolUrine: boolean[],
    urination: boolean[],
    percents: number[],
    means: number[],
): void {
    // 閾値の設定（インデックス間の許容最大距離）
    const DISTANCE_THRESHOLD = 4;

    // poolUrineのtrueのインデックスを取得
    const poolUrineIndices = poolUrine
        .map((value, index) => value ? index : -1)
        .filter((index) => index !== -1);

    // urinationのtrueのインデックスとその値を処理
    for (let i = 0; i < urination.length; i++) {
        if (!urination[i]) continue;

        // i未満のpoolUrineIndicesから最も近いものを探す
        const previousPoolIndices = poolUrineIndices.filter((index) => index < i);
        if (previousPoolIndices.length === 0) continue;

        const nearestPoolIndex = Math.max(...previousPoolIndices);
        const distance = i - nearestPoolIndex;

        // 距離が閾値を超える場合、移動を検討
        if (distance > DISTANCE_THRESHOLD) {

            const startIndex = nearestPoolIndex + 1;
            const endIndex = startIndex + DISTANCE_THRESHOLD;

            let bestIndex = startIndex;
            let minPercent = percents[bestIndex];
            let minMean = means[bestIndex];

            for (let j = startIndex; j < endIndex; j++) {
                if (percents[j] < minPercent ||
                    (percents[j] === minPercent && means[j] < minMean)) {
                    bestIndex = j;
                    minPercent = percents[j];
                    minMean = means[j];
                }
            }
            urination[i] = false;
            urination[bestIndex] = true;
        }
    }
}

/**
 * 閾値以上のrank(=rankが低い)のflagをfalseに変更する
 * @param targets - 真偽値の配列
 * @param ranks - ランクの配列
 */
function dropLowRankFlags(
    targets: boolean[], ranks: number[]
): void {
    const RANK_THRESHOLD = targets.length > 24 ? 7 : 4;

    const _iteratedRanks = ranks[Symbol.iterator]();
    let _rank: number | null = null;
    let _startIndex = 0;

    [...targets, false].forEach((target: boolean, i) => {

        if (target && _rank === null) {
            _rank = _iteratedRanks.next().value;
            _startIndex = i;
        } else if (!target && _rank !== null) {
            if (_rank > RANK_THRESHOLD) {
                targets.fill(false, _startIndex, i);
            }
            _rank = null;
        }
    });
}

/**
 * poolUrineとurination flagが同じindexでconflictした際に、urination側のflagをfalseに変更する
 * @param poolUrine - プール内の尿検出の真偽値配列
 * @param urination - 排尿イベントの真偽値配列（更新対象）
 */
function dropConflictedUrination(poolUrine: boolean[], urination: boolean[]
): void {
  for (let i = 0; i < poolUrine.length; i++) {
    if (poolUrine[i] && urination[i]) {
      urination[i] = false;
    }
  }
}
